import { useCallback } from 'react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { /*Language,*/ TranslationsType } from '@/types'; // Language type removed

/**
 * Custom hook for handling internationalization (i18n).
 * Provides access to the current language, translation function, and loading state.
 * It leverages the LanguageContext to get the necessary data.
 *
 * @returns {{
 *   t: (key: string, params?: Record<string, string>) => string;
 *   isLoading: boolean;
 *   currentLanguage: Language;
 * }} An object containing:
 *   - `t`: A function to translate a given key. It supports nested keys (e.g., 'common.submit')
 *          and optional parameter interpolation using `{{paramName}}` syntax.
 *          If a key is not found, it logs a warning and returns the key itself.
 *   - `isLoading`: A boolean indicating if the language context (and translations) are still loading.
 *   - `currentLanguage`: The currently active language code (e.g., 'en', 'es').
 */
export function useTranslation() {
    const {
        language,
        translations: currentTranslations, // Renamed for clarity
        isLoading,
    } = useLanguage();

    // Memoize the t function for both loading and loaded states
    const t = useCallback(
        (
            key: string,
            params?: Record<string, string>,
            options?: { fallback?: string }
        ): string => {
            // If still loading, just return the key or fallback
            if (isLoading) {
                return options?.fallback ?? key;
            }

            const keys = key.split('.');
            // Access currentTranslations directly as it holds the active language's data
            const translation = keys.reduce<
                Record<string, unknown> | string | undefined
            >(
                (obj, k) => {
                    if (obj && typeof obj === 'object') {
                        return obj[k] as
                            | Record<string, unknown>
                            | string
                            | undefined;
                    }
                    return undefined;
                },
                currentTranslations as TranslationsType | undefined // Start with the loaded translations
            );

            if (translation === undefined || translation === null) {
                // console.warn(`[useTranslation] Translation key "${key}" not found for language "${language}"`);
                return options?.fallback ?? key;
            }

            if (typeof translation !== 'string') {
                console.warn(
                    `[useTranslation] Translation key "${key}" is not a string for language "${language}"`
                );
                return options?.fallback ?? key;
            }

            let translatedString = translation;
            if (params) {
                translatedString = Object.entries(params).reduce(
                    (str, [paramKey, value]) =>
                        str.replace(new RegExp(`{{${paramKey}}}`, 'g'), value),
                    translatedString
                );
            }
            return translatedString;
        },
        [isLoading, currentTranslations, language] // Dependencies for useCallback
    );

    return { t, isLoading, currentLanguage: language };
}
